import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE}/api`;

const QUEUE_KEY = 'pos_offline_queue';

// BUG-F11 fix: safe JSON parser. If localStorage value is corrupted,
// parse silently falls back to defaultValue instead of throwing and
// breaking every subsequent API call (single-point-of-failure).
function safeParse(raw, defaultValue) {
  if (raw == null || raw === '') return defaultValue;
  try {
    return JSON.parse(raw);
  } catch {
    return defaultValue;
  }
}

const api = axios.create({ baseURL: API });

// 🔒 MULTI-BRANCH-FASE2-2026-05 — Global interceptor que añade X-Branch-Id
// a TODA petición axios (incluidos los lugares donde se importa axios crudo).
// Si no hay branch seleccionado o el modo multi está desactivado, no agrega
// nada y el backend opera en modo single (comportamiento legacy).
axios.interceptors.request.use((config) => {
  try {
    const enabled = localStorage.getItem('multi_branch_enabled') === 'true';
    if (!enabled) return config;
    const active = localStorage.getItem('active_branch_id');
    if (active && active.trim()) {
      config.headers = config.headers || {};
      config.headers['X-Branch-Id'] = active;
    }
  } catch {}
  return config;
});

// 🔒 STATION-REGISTRY-FASE1-2026-09-10 — Identificador de equipo.
// Se genera UNA vez por dispositivo y se guarda en localStorage. Mismo patron
// que 'vexly_terminal_id' en CashRegister.js. Los navegadores NO exponen la MAC
// ni ningun id de hardware, asi que este es el unico camino posible.
// FASE 1: solo se envia y se registra. NO bloquea ni valida nada todavia.
const DEVICE_KEY = 'vexly_device_id';

function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (id && id.trim()) return id;
    const rnd = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    id = `DEV-${rnd}`;
    localStorage.setItem(DEVICE_KEY, id);
    return id;
  } catch {
    return '';
  }
}

// Van DOS interceptores a proposito: una instancia creada con axios.create()
// NO hereda los interceptores del axios global. Sin los dos, la cabecera no
// viajaria en las llamadas que pasan por `api` — incluido el login.
// Son interceptores NUEVOS e independientes: no se toca el de X-Branch-Id
// (que tiene un early return) ni el del token.
axios.interceptors.request.use((config) => {
  try {
    const dev = getDeviceId();
    if (dev) {
      config.headers = config.headers || {};
      config.headers['X-Vexly-Device'] = dev;
    }
  } catch {}
  return config;
});

api.interceptors.request.use((config) => {
  try {
    const dev = getDeviceId();
    if (dev) {
      config.headers = config.headers || {};
      config.headers['X-Vexly-Device'] = dev;
    }
  } catch {}
  return config;
});

// Token interceptor
api.interceptors.request.use((config) => {
  let token = null;
  try { token = localStorage.getItem('pos_token'); } catch {}
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Offline queue + auth interceptor
api.interceptors.response.use(
  (res) => res,
  (err) => {
    // BUG-F13 fix: handle 401 (token expired/invalid) by clearing session
    // and redirecting to /login. Skip on /auth/* requests so the login page
    // can show its own error inline instead of bouncing.
    // 🔒 VOID-AUTH-2026-02 — 403 (sin permiso) NUNCA debe redirigir; el modal
    // de anulación / autorización lo maneja inline. Sólo 401 (token inválido).
    const status = err.response?.status;
    const reqUrl = err.config?.url || '';
    if (status === 401 && !reqUrl.includes('/auth/')) {
      try { localStorage.removeItem('pos_token'); } catch {}
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.replace('/login');
      }
    }

    if (!navigator.onLine && err.config && ['post', 'put', 'delete'].includes(err.config.method)) {
      try {
        const queue = safeParse(localStorage.getItem(QUEUE_KEY), []);
        queue.push({ method: err.config.method, url: err.config.url, data: err.config.data });
        localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
      } catch {}
    }
    return Promise.reject(err);
  }
);

// 🔒 MULTI-BRANCH-FASE2-2026-05 — Interceptor GLOBAL para errores de sucursal.
// Aplica también a llamadas con axios crudo (no solo al instance `api`).
// Si el backend rechaza con BRANCH_AGGREGATE / BRANCH_REQUIRED / BRANCH_NOT_ALLOWED,
// emitimos un evento custom (`branch-error`) que la app puede mostrar como toast
// y dejamos que la promesa siga rechazada para que la página que llamó pueda
// manejar el error de forma normal.
//
// 🔒 MOVE-PERMS-2026-05 — Adicionalmente, capturamos 403 PERMISSION_DENIED.
// Antes el frontend dejaba pasar 403 silenciosamente (los botones quedaban sin
// feedback). Ahora emitimos `permission-denied` para mostrar toast genérico.
// El handler local de cada componente puede detenerlo poniendo `e.preventDefault()`
// si quiere mostrar su propio modal (caso PIN override).
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    try {
      const detail = err?.response?.data?.detail;
      const code = detail && typeof detail === 'object' ? detail.code : null;
      const message = detail && typeof detail === 'object' ? detail.message : null;
      if (code && ['BRANCH_AGGREGATE', 'BRANCH_REQUIRED', 'BRANCH_NOT_ALLOWED', 'BRANCH_INVALID'].includes(code)) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('branch-error', { detail: { code, message } }));
        }
      }
      // 🔒 MOVE-PERMS-2026-05 — Detección genérica de 403 PERMISSION_DENIED.
      // El componente que disparó la request puede escuchar el evento y mostrar
      // el modal de PIN override. Si nadie escucha o nadie llama
      // preventDefault, mostramos el toast genérico fallback.
      if (err?.response?.status === 403 && typeof window !== 'undefined') {
        const evt = new CustomEvent('permission-denied', {
          detail: {
            code: code || 'PERMISSION_DENIED',
            permission: detail && typeof detail === 'object' ? detail.permission : null,
            message: message || 'No tienes permiso para esta acción.',
            requiresPinOverride: !!(detail && typeof detail === 'object' && detail.requires_pin_override),
            url: err?.config?.url,
          },
          cancelable: true,
        });
        const dispatched = window.dispatchEvent(evt);
        if (dispatched && !evt.defaultPrevented) {
          // Fallback: toast genérico si nadie capturó el evento
          window.dispatchEvent(new CustomEvent('app-toast', {
            detail: { variant: 'destructive', title: 'Sin permiso',
                      description: message || 'No tienes permiso para esta acción.' },
          }));
        }
      }
    } catch {}
    return Promise.reject(err);
  }
);

// Process offline queue when back online
export async function processOfflineQueue() {
  const queue = safeParse(localStorage.getItem(QUEUE_KEY), []);
  if (queue.length === 0) return;
  const failed = [];
  for (const req of queue) {
    try {
      await api({ method: req.method, url: req.url, data: req.data ? safeParse(req.data, undefined) : undefined });
    } catch {
      failed.push(req);
    }
  }
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(failed)); } catch {}
}

// Format money RD$
export function formatMoney(amount) {
  return `RD$ ${new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0)}`;
}

// Auth
export const authAPI = {
  login: (pin, managerPin) => api.post('/auth/login', managerPin ? { pin, manager_pin: managerPin } : { pin }),  // 🔒 SESION-UNICA-2026-10-03
  me: () => api.get('/auth/me'),
  activeSessions: () => api.get('/auth/active-sessions'),
  revokeSession: (userId) => api.post(`/auth/revoke-session/${userId}`),
  revokeInactiveSessions: (dryRun) => api.post(`/auth/revoke-inactive-sessions${dryRun ? '?dry_run=true' : ''}`),  // 🔒 SESIONES-INACTIVAS-2026-10-05
  getAutoLogoutConfig: () => api.get('/auth/auto-logout-config'),
  updateAutoLogoutConfig: (data) => api.put('/auth/auto-logout-config', data),
  roles: () => api.get('/roles'),  // 🔒 AUTOLOGOUT-POR-PUESTO-2026-10-03
  getSingleSessionConfig: () => api.get('/auth/single-session-config'),  // 🔒 SESION-UNICA-2026-10-03
  updateSingleSessionConfig: (enabled) => api.put('/auth/single-session-config', { enabled }),
  // 🔒 MOVE-PERMS-2026-05 — Verifica PIN externo para autorización temporal.
  // Returns { ok, user_id, user_name, role, role_level } when valid.
  verifyPin: (pin, requiredPermission) =>
    api.post('/auth/verify-pin', { pin, required_permission: requiredPermission }),
};

// Seed
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Areas
export const areasAPI = {
  list: () => api.get('/areas'),
  create: (data) => api.post('/areas', data),
  update: (id, data) => api.put(`/areas/${id}`, data),
  delete: (id) => api.delete(`/areas/${id}`),
  duplicate: (id, newName) => api.post(`/areas/${id}/duplicate`, { new_name: newName }),
};

// Tables (with offline localStorage cache)
export const tablesAPI = {
  list: (areaId) => api.get('/tables', { params: areaId ? { area_id: areaId } : {} })
    .then(res => {
      try { localStorage.setItem('vexly_mesas', JSON.stringify(res.data)); } catch {}
      return res;
    })
    .catch(err => {
      if (!err.response) {
        const cached = safeParse(localStorage.getItem('vexly_mesas'), null);
        if (cached) return { data: cached };
      }
      throw err;
    }),
  create: (data) => api.post('/tables', data),
  createBulk: (data) => api.post('/tables/bulk', data),
  update: (id, data) => api.put(`/tables/${id}`, data),
  delete: (id) => api.delete(`/tables/${id}`),
};

// Map Decorators
export const decoratorsAPI = {
  list: (areaId) => api.get('/decorators', { params: areaId ? { area_id: areaId } : {} }),
  create: (data) => api.post('/decorators', data),
  update: (id, data) => api.put(`/decorators/${id}`, data),
  delete: (id) => api.delete(`/decorators/${id}`),
};

// Device-specific Map Layouts (desktop vs mobile)
export const layoutsAPI = {
  get: (areaId, deviceType) => api.get(`/layouts/${areaId}/${deviceType}`),
  save: (areaId, deviceType, data) => api.put(`/layouts/${areaId}/${deviceType}`, data),
  delete: (areaId, deviceType) => api.delete(`/layouts/${areaId}/${deviceType}`),
};

// Categories
export const categoriesAPI = {
  list: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Products
export const productsAPI = {
  list: (categoryId) => api.get('/products', { params: categoryId ? { category_id: categoryId } : {} }),
  // 🔒 TALLER-PRODUCTOS-2026-09-12 — Configuracion lista los DOS tipos: sin
  // `taller: 'all'` un servicio de taller desapareceria de esta pantalla apenas
  // se marca y no se podria volver a editar.
  listAll: () => api.get('/products', { params: { include_inactive: true, taller: 'all' } }),
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.put(`/products/${id}`, { active: false }),
  // 🔒 MENU-REORDER-FASE2-2026-08-21 — { category_id, product_ids[] }
  reorder: (data) => api.put('/products/reorder', data),
};

// Modifiers
export const modifiersAPI = {
  list: () => api.get('/modifiers'),
  get: (id) => api.get(`/modifiers/${id}`),
  create: (data) => api.post('/modifiers', data),
  update: (id, data) => api.put(`/modifiers/${id}`, data),
  delete: (id) => api.delete(`/modifiers/${id}`),
};

// Report Categories (for product classification)
export const reportCategoriesAPI = {
  list: () => api.get('/report-categories'),
  create: (data) => api.post('/report-categories', data),
  update: (id, data) => api.put(`/report-categories/${id}`, data),
  delete: (id) => api.delete(`/report-categories/${id}`),
};

// Orders (with offline localStorage cache)
export const ordersAPI = {
  list: (params) => api.get('/orders', { params }),
  get: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  addItems: (id, items) => api.post(`/orders/${id}/items`, { items }),
  updateItem: (orderId, itemId, data) => api.put(`/orders/${orderId}/items/${itemId}`, data),
  cancelItem: (orderId, itemId, data) => api.post(`/orders/${orderId}/cancel-item/${itemId}`, data),
  cancelItems: (orderId, data) => api.post(`/orders/${orderId}/cancel-items`, data),
  sendToKitchen: (id) => api.post(`/orders/${id}/send-kitchen`),
  setServiceType: (id, service_type) => api.patch(`/orders/${id}/service-type`, { service_type }),
  setItemKitchenNote: (orderId, itemId, kitchen_note) => api.patch(`/orders/${orderId}/items/${itemId}/kitchen-note`, { kitchen_note }),
  // 🔒 MOVE-PERMS-2026-05 — Las 5 operaciones aceptan `authorizedBy` opcional
  // { user_id, user_name } que se inyecta como authorized_by_id / _name al body
  // cuando el solicitante no tiene permiso directo y un supervisor autorizó.
  moveToTable: (orderId, targetTableId, merge = false, authorizedBy = null) =>
    api.post(`/orders/${orderId}/move`, {
      target_table_id: targetTableId, merge,
      ...(authorizedBy ? { authorized_by_id: authorizedBy.user_id, authorized_by_name: authorizedBy.user_name } : {}),
    }),
  splitToNewOrder: (orderId, itemIds, label = '', authorizedBy = null) =>
    api.post(`/orders/${orderId}/split`, {
      item_ids: itemIds, label,
      ...(authorizedBy ? { authorized_by_id: authorizedBy.user_id, authorized_by_name: authorizedBy.user_name } : {}),
    }),
  getTableOrders: (tableId) => api.get(`/tables/${tableId}/orders`)
    .then(res => {
      try {
        // Merge: keep orders from other tables, replace orders for this table
        const existing = safeParse(localStorage.getItem('vexly_orders'), []);
        const other = Array.isArray(existing) ? existing.filter(o => o.table_id !== tableId) : [];
        localStorage.setItem('vexly_orders', JSON.stringify([...other, ...(res.data || [])]));
      } catch {}
      return res;
    })
    .catch(err => {
      if (!err.response) {
        const all = safeParse(localStorage.getItem('vexly_orders'), null);
        if (Array.isArray(all)) {
          return { data: all.filter(o => o.table_id === tableId) };
        }
      }
      throw err;
    }),
  // Quick Orders (Orden Rápida)
  createQuick: (customer_name = null, service_type = 'dine_in') => api.post('/orders/quick', { customer_name, service_type }),
  listQuickActive: () => api.get('/orders/quick/active'),
  setQuickStatus: (id, status) => api.patch(`/orders/quick/${id}/status`, { status }),
  createNewAccount: (tableId, label = '') => api.post(`/tables/${tableId}/new-account`, { label }),
  deleteEmpty: (orderId) => api.delete(`/orders/${orderId}/empty`),
  mergeOrders: (sourceOrderId, targetOrderId, authorizedBy = null) =>
    api.post(`/orders/${sourceOrderId}/merge/${targetOrderId}`,
      authorizedBy
        ? { authorized_by_id: authorizedBy.user_id, authorized_by_name: authorizedBy.user_name }
        : {}
    ),
  moveAllToTable: (sourceTableId, targetTableId, authorizedBy = null) =>
    api.post(`/tables/${sourceTableId}/move-all`, {
      target_table_id: targetTableId,
      ...(authorizedBy ? { authorized_by_id: authorizedBy.user_id, authorized_by_name: authorizedBy.user_name } : {}),
    }),
  moveItems: (orderId, targetOrderId, itemIds, quantities, authorizedBy = null) =>
    api.post(`/orders/${orderId}/move-items`, {
      target_order_id: targetOrderId, item_ids: itemIds, quantities,
      ...(authorizedBy ? { authorized_by_id: authorizedBy.user_id, authorized_by_name: authorizedBy.user_name } : {}),
    }),
};

// Void Audit Logs
export const voidAuditAPI = {
  list: (params) => api.get('/void-audit-logs', { params }),
};

// Kitchen
export const kitchenAPI = {
  orders: () => api.get('/kitchen/orders'),
  updateItem: (orderId, itemId, data) => api.put(`/kitchen/items/${orderId}/${itemId}`, data),
  ackCancel: (orderId, itemId) => api.post(`/kitchen/items/${orderId}/${itemId}/ack-cancel`),  // 🔒 KDS-ANULADO-2026-10-05
  config: () => api.get('/kitchen/config'),  // 🔒 KDS-SEMAFORO-2026-10-05
};

// Bills
export const billsAPI = {
  list: (params) => api.get('/bills', { params }),
  get: (id) => api.get(`/bills/${id}`),
  create: (data) => api.post('/bills', data),
  pay: (id, data) => api.post(`/bills/${id}/pay`, data),
  cancel: (id) => api.post(`/bills/${id}/cancel`),
};

// Cancellation Reasons
export const reasonsAPI = {
  list: () => api.get('/cancellation-reasons'),
  create: (data) => api.post('/cancellation-reasons', data),
  update: (id, data) => api.put(`/cancellation-reasons/${id}`, data),
};

// Shifts (Legacy MongoDB)
export const shiftsAPI = {
  list: () => api.get('/shifts'),
  current: () => api.get('/shifts/current'),
  open: (data) => api.post('/shifts/open', data),
  close: (id, data) => api.put(`/shifts/${id}/close`, data),
};

// POS Sessions (Supabase)
export const posSessionsAPI = {
  health: () => api.get('/pos-sessions/health'),
  check: () => api.get('/pos-sessions/check'),
  current: () => api.get('/pos-sessions/current'),
  open: (data) => api.post('/pos-sessions/open', data),
  close: (id, data) => api.put(`/pos-sessions/${id}/close`, data),
  history: (params) => api.get('/pos-sessions/history', { params }),
  terminals: () => api.get('/pos-sessions/terminals'),
  terminalsInUse: () => api.get('/pos-sessions/terminals/in-use'),
  movementReasons: () => api.get('/pos-sessions/movement-reasons'),
  openShifts: () => api.get('/pos-sessions/open-shifts'), // Para verificar turnos abiertos antes de Cierre Z
  // Movements
  addMovement: (sessionId, data) => api.post(`/pos-sessions/${sessionId}/movements`, data),
  getMovements: (sessionId) => api.get(`/pos-sessions/${sessionId}/movements`),
  registerSale: (sessionId, amount, method) => api.put(`/pos-sessions/${sessionId}/register-sale`, null, { params: { amount, payment_method: method } }),
  salesBreakdown: (sessionId) => api.get(`/pos-sessions/${sessionId}/sales-breakdown`),
};

// Simple Inventory
export const simpleInventoryAPI = {
  list: () => api.get('/simple-inventory'),
  adjust: (productId, data) => api.put(`/simple-inventory/${productId}/adjust`, data),
  auditLog: (params) => api.get('/simple-inventory/audit-log', { params }),
  exportCsv: (params) => api.get('/simple-inventory/audit-log/export-csv', { params, responseType: 'blob' }),
  productsWithSimple: () => api.get('/simple-inventory/products-with-simple'),
  pendingAlerts: () => api.get('/simple-inventory/alerts/pending'),
  dismissAlerts: (productIds) => api.post('/simple-inventory/alerts/dismiss', { product_ids: productIds }),
};

// Business Days (Jornadas de Trabajo)
export const businessDaysAPI = {
  // Estado actual
  check: () => api.get('/business-days/check'),
  current: () => api.get('/business-days/current'),
  // Operaciones
  authorize: (pin, action) => api.post('/business-days/authorize', { pin, action }),
  open: (data) => api.post('/business-days/open', data),
  close: (data) => api.post('/business-days/close', data),
  // 🔒 PLAN-B-CIERRE-FORZADO-2026-05-08 — Preview antes de cerrar (no muta nada)
  closePreview: () => api.get('/business-days/close-preview'),
  // Historial
  history: (params) => api.get('/business-days/history', { params }),
  get: (id) => api.get(`/business-days/${id}`),
  getTransactions: (id) => api.get(`/business-days/${id}/transactions`),
  // Reportes X y Z
  reportZ: (dayId) => api.get(`/business-days/${dayId}/report-z`),
  reportZCurrent: () => api.get('/business-days/current/report-z'),
  reportX: (sessionId) => api.get(`/business-days/session/${sessionId}/report-x`),
};

// Tax Configuration (Sistema de Impuestos Dinámico)
export const taxesAPI = {
  // Config
  getConfigs: () => api.get('/taxes/config'),
  createConfig: (data) => api.post('/taxes/config', data),
  updateConfig: (code, data) => api.put(`/taxes/config/${code}`, data),
  deleteConfig: (code) => api.delete(`/taxes/config/${code}`),
  seedDefaults: () => api.post('/taxes/seed-defaults'),
  // Product taxes
  getProductTaxes: (productId) => api.get(`/taxes/products/${productId}`),
  assignTax: (data) => api.post('/taxes/products/assign', data),
  removeTax: (productId, taxCode) => api.delete(`/taxes/products/${productId}/${taxCode}`),
  bulkAssign: (productIds, taxCode) => api.post('/taxes/products/bulk-assign', { product_ids: productIds, tax_code: taxCode }),
  assignToCategory: (categoryId, taxCode) => api.post('/taxes/category/assign', null, { params: { category_id: categoryId, tax_code: taxCode } }),
  // Calculation
  calculate: (productId, quantity, unitPrice, isDelivery = false) => 
    api.post('/taxes/calculate', null, { params: { product_id: productId, quantity, unit_price: unitPrice, is_delivery: isDelivery } }),
  // Report
  getTaxSummary: (params) => api.get('/taxes/report/summary', { params }),
};

// NCF - Comprobantes Fiscales (Supabase)
export const ncfAPI = {
  // Types
  getTypes: () => api.get('/ncf/types'),
  getType: (code) => api.get(`/ncf/types/${code}`),
  // Sequences CRUD
  getSequences: (activeOnly = true, includeAlerts = true) => 
    api.get('/ncf/sequences', { params: { active_only: activeOnly, include_alerts: includeAlerts } }),
  getSequence: (id) => api.get(`/ncf/sequences/${id}`),
  createSequence: (data) => api.post('/ncf/sequences', data),
  updateSequence: (id, data) => api.put(`/ncf/sequences/${id}`, data),
  deleteSequence: (id) => api.delete(`/ncf/sequences/${id}`),
  // Generation
  generate: (ncfTypeCode, billTotal) => 
    api.post(`/ncf/generate/${ncfTypeCode}`, null, { params: { bill_total: billTotal } }),
  // Return reasons
  getReturnReasons: () => api.get('/ncf/return-reasons'),
  // Alerts
  getAlerts: () => api.get('/ncf/alerts'),
};

// Table Movements Audit
export const tableMovementsAPI = {
  list: (params) => api.get('/reports/table-movements', { params }),
  stats: (date) => api.get('/reports/table-movements/stats', { params: date ? { date } : {} }),
};

// ─── INVENTORY MANAGEMENT ───

// Ingredients
export const ingredientsAPI = {
  list: (category) => api.get('/ingredients', { params: { limit: 5000, ...(category ? { category } : {}) } }), // 🔒 LIMITES-LISTADOS-2026-08-25
  get: (id) => api.get(`/ingredients/${id}`),
  create: (data) => api.post('/ingredients', data),
  update: (id, data) => api.put(`/ingredients/${id}`, data),
  delete: (id) => api.delete(`/ingredients/${id}`),
  getAffectedRecipes: (id) => api.get(`/ingredients/${id}/affected-recipes`),
  getAuditLogs: (id, limit = 50) => api.get(`/ingredients/${id}/audit-logs`, { params: { limit } }),
  getAllAuditLogs: (params) => api.get('/ingredients/audit-logs/all', { params }),
  getUnifiedAudit: (params) => api.get('/inventory/audit-unified', { params }),  // 🔒 AUDIT-UNIFIED-FASE1B-2026-08-10
  getConversionAnalysis: (id) => api.get(`/ingredients/${id}/conversion-analysis`),
};

// 🔒 CATEGORIAS-INSUMO-UI-2026-08-25
export const ingredientCategoriesAPI = {
  list: () => api.get('/ingredient-categories'),
  // 🔒 CATEGORIAS-GESTION-2026-08-25
  create: (data) => api.post('/ingredient-categories', data),
  update: (id, data) => api.put(`/ingredient-categories/${id}`, data),
  delete: (id) => api.delete(`/ingredient-categories/${id}`),
};

// Unit Definitions (Custom Units)
export const unitDefinitionsAPI = {
  list: () => api.get('/unit-definitions'),
  create: (data) => api.post('/unit-definitions', data),
  update: (id, data) => api.put(`/unit-definitions/${id}`, data),
  delete: (id) => api.delete(`/unit-definitions/${id}`),
};

// Stock
export const stockAPI = {
  list: (params) => api.get('/stock', { params }),
  listMultilevel: (params) => api.get('/stock/multilevel', { params }),
  byIngredient: (ingredientId) => api.get(`/stock/by-ingredient/${ingredientId}`),
  upsert: (data) => api.post('/stock', data),
  transfer: (data) => api.post('/stock/transfer', data),
  waste: (data) => api.post('/stock/waste', data),
  difference: (data) => api.post('/stock/difference', data),
  listDifferences: (params) => api.get('/stock/differences', { params }),
  adjust: (data) => api.post('/inventory/adjust', data),
  alerts: () => api.get('/inventory/alerts'),
};

// Stock Movements
export const stockMovementsAPI = {
  list: (params) => api.get('/stock-movements', { params }),
};

// Stock Alerts
export const stockAlertsAPI = {
  check: (sendEmail = false) => api.get('/inventory/check-alerts', { params: { send_email: sendEmail } }),
  getConfig: () => api.get('/inventory/alert-config'),
  updateConfig: (data) => api.put('/inventory/alert-config', data),
  getLogs: (limit = 20) => api.get('/inventory/alert-logs', { params: { limit } }),
  getSchedulerStatus: () => api.get('/inventory/scheduler-status'),
};

// Warehouses
export const warehousesAPI = {
  list: () => api.get('/warehouses'),
  create: (data) => api.post('/warehouses', data),
  update: (id, data) => api.put(`/warehouses/${id}`, data),
  delete: (id) => api.delete(`/warehouses/${id}`),
};

// Suppliers
export const suppliersAPI = {
  list: () => api.get('/suppliers'),
  get: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Recipes
export const recipesAPI = {
  list: () => api.get('/recipes'),
  get: (productId) => api.get(`/recipes/product/${productId}`),
  create: (data) => api.post('/recipes', data),
  update: (id, data) => api.put(`/recipes/${id}`, data),
  delete: (id) => api.delete(`/recipes/${id}`),
  deleteForce: (id) => api.delete(`/recipes/${id}`, { params: { force: true } }),
  deleteByProduct: (productId) => api.delete(`/recipes/product/${productId}`),
  getCost: (productId) => api.get(`/inventory/recipe-cost/${productId}`),
  recalculateCosts: () => api.post('/inventory/recalculate-costs'),
  getHistory: (recipeId) => api.get(`/recipes/${recipeId}/history`),
  getAllHistory: (params) => api.get('/recipes/history/all', { params }),
};

// Inventory Explosion
export const inventoryExplosionAPI = {
  deductForProduct: (data) => api.post('/inventory/deduct-for-product', data),
  checkAvailability: (data) => api.post('/inventory/check-availability', data),
};

// Sub-recipe Production
export const productionAPI = {
  listSubrecipes: () => api.get('/inventory/subrecipes'),
  checkProduction: (data) => api.post('/inventory/check-production', data),
  produce: (data) => api.post('/inventory/produce', data),
  getHistory: (params) => api.get('/inventory/production-history', { params }),
};

// Inventory Settings
export const inventorySettingsAPI = {
  get: () => api.get('/inventory/settings'),
  update: (data) => api.put('/inventory/settings', data),
  getReorderAlerts: () => api.get('/inventory/reorder-alerts'),
  getProductsStockStatus: (warehouseId) => api.get('/inventory/products-stock', { params: warehouseId ? { warehouse_id: warehouseId } : {} }),
  getProductStockStatus: (productId, warehouseId) => api.get(`/inventory/product-stock/${productId}`, { params: warehouseId ? { warehouse_id: warehouseId } : {} }),
};

// 🔍 SCAN-INVENTORY-2026-05-16 — Conteo físico por escaneo
export const scanInventoryAPI = {
  createSession: (data) => api.post('/scan-inventory/sessions', data),
  listSessions: (params) => api.get('/scan-inventory/sessions', { params }),
  getSession: (id) => api.get(`/scan-inventory/sessions/${id}`),
  completeSession: (id) => api.put(`/scan-inventory/sessions/${id}/complete`),
  cancelSession: (id) => api.put(`/scan-inventory/sessions/${id}/cancel`),
  scan: (data) => api.post('/scan-inventory/scan', data),
  applyAdjustments: (id) => api.post(`/scan-inventory/sessions/${id}/apply-adjustments`),
  varianceReport: (sessionId) => api.get('/scan-inventory/variance-report', { params: { session_id: sessionId }}),
  getSettings: () => api.get('/scan-inventory/settings'),
  updateSettings: (data) => api.put('/scan-inventory/settings', data),
};

export const ingredientBarcodeAPI = {
  getByBarcode: (barcode, warehouseId) =>
    api.get(`/ingredients/by-barcode/${encodeURIComponent(barcode)}`, {
      params: warehouseId ? { warehouse_id: warehouseId } : {},
    }),
};

// Purchase Orders
export const purchaseOrdersAPI = {
  list: (params) => api.get('/purchase-orders', { params }),
  get: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  updateStatus: (id, status) => api.put(`/purchase-orders/${id}/status`, null, { params: { status } }),
  receive: (id, data) => api.post(`/purchase-orders/${id}/receive`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
};

// Shopping Assistant & Cost Control
export const purchasingAPI = {
  getSuggestions: (params) => api.get('/purchasing/suggestions', { params }),
  generatePO: (data) => api.post('/purchasing/generate-po', data),
  getPriceAlerts: () => api.get('/purchasing/price-alerts'),
  recalculateMargins: () => api.post('/purchasing/recalculate-recipe-margins'),
  getIngredientPriceHistory: (id, limit = 50) => api.get(`/ingredients/${id}/price-history`, { params: { limit } }),
};

// Reports
export const reportsAPI = {
  inventoryValuation: (params) => api.get('/reports/inventory-valuation', { params }),
  valuationTrends: (params) => api.get('/reports/valuation-trends', { params }),
  inventory: () => api.get('/reports/inventory'),
  profit: (date) => api.get('/reports/profit', { params: date ? { date } : {} }),
  voids: (params) => api.get('/reports/voids', { params }),
  stockAdjustments: (params) => api.get('/reports/stock-adjustments', { params }),
};

export default api;
